import { useState } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Copy, RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';

const FindAndReplaceTool = () => {
  const [text, setText] = useState('');
  const [find, setFind] = useState('');
  const [replace, setReplace] = useState('');
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [useRegex, setUseRegex] = useState(false);

  const doReplace = () => {
    if (!text || !find) return { result: text, count: 0 };
    
    try {
      let count = 0;
      let result;
      
      if (useRegex) {
        const flags = caseSensitive ? 'g' : 'gi';
        const regex = new RegExp(find, flags);
        result = text.replace(regex, () => {
          count++;
          return replace;
        });
      } else {
        const flags = caseSensitive ? 'g' : 'gi';
        const escaped = find.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(escaped, flags);
        result = text.replace(regex, () => {
          count++;
          return replace;
        });
      }
      
      return { result, count };
    } catch (e) {
      return { result: text, count: 0, error: e.message };
    }
  };

  const { result, count, error } = doReplace();

  const copyResult = () => {
    navigator.clipboard.writeText(result);
    toast.success('Result copied!');
  };

  const applyChanges = () => {
    setText(result);
    toast.success(`Replaced ${count} occurrences`);
  };

  return (
    <div className="space-y-6" data-testid="find-replace-tool">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-sm text-muted-foreground mb-2 block">Find</label>
          <Input
            placeholder="Text to find..."
            value={find}
            onChange={(e) => setFind(e.target.value)}
          />
        </div>
        <div>
          <label className="text-sm text-muted-foreground mb-2 block">Replace With</label>
          <Input
            placeholder="Replacement text..."
            value={replace}
            onChange={(e) => setReplace(e.target.value)}
          />
        </div>
      </div>

      <div className="flex gap-4 flex-wrap">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={caseSensitive}
            onChange={(e) => setCaseSensitive(e.target.checked)}
            className="rounded"
          />
          <span className="text-sm">Case Sensitive</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={useRegex}
            onChange={(e) => setUseRegex(e.target.checked)}
            className="rounded"
          />
          <span className="text-sm">Use Regex</span>
        </label>
        {find && (
          <span className="text-sm text-muted-foreground">
            {count} match{count !== 1 ? 'es' : ''} found
          </span>
        )}
        {error && <span className="text-sm text-red-400">Regex error: {error}</span>}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-sm text-muted-foreground mb-2 block">Original Text</label>
          <Textarea
            placeholder="Enter your text here..."
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="min-h-[250px] font-mono text-sm bg-muted/50"
          />
        </div>
        <div>
          <label className="text-sm text-muted-foreground mb-2 block">Preview Result</label>
          <Textarea
            value={result}
            readOnly
            className="min-h-[250px] font-mono text-sm bg-muted/50"
          />
        </div>
      </div>

      <div className="flex gap-2">
        <Button onClick={applyChanges} disabled={!find || count === 0}>
          <Search className="w-4 h-4 mr-2" /> Apply Replace ({count})
        </Button>
        <Button onClick={copyResult} variant="outline" disabled={!result}>
          <Copy className="w-4 h-4 mr-2" /> Copy Result
        </Button>
        <Button onClick={() => { setText(''); setFind(''); setReplace(''); }} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" /> Clear All
        </Button>
      </div>
    </div>
  );
};

export default FindAndReplaceTool;
